import { Link } from 'react-router-dom';
import { UserCheck, Lightbulb, Clock, DollarSign, TrendingUp,
  ShoppingCart, Package, Users, AlertTriangle } from 'lucide-react';
import { AdminCard, AdminKPI, AdminEmpty } from '../../components/admin/AdminPrimitives';

const IMG_FALLBACK = 'https://images.unsplash.com/photo-1541643600914-78b084683601?w=100';
const onImgErr = (e) => { e.target.src = IMG_FALLBACK; };

// ---------- Alerts ----------

export const PendingUsersAlert = ({ count }) => (
  <Link
    to="/admin/utilisateurs"
    className="block bg-orange-500/10 border border-orange-500/30 p-4 mb-6 hover:border-orange-500/50 transition-colors"
    data-testid="pending-users-alert"
  >
    <div className="flex items-center gap-3">
      <UserCheck className="w-5 h-5 text-orange-400 flex-shrink-0" />
      <div>
        <p className="text-orange-400 font-medium">
          {count} compte{count > 1 ? 's' : ''} en attente de vérification
        </p>
        <p className="text-zinc-500 text-sm">Cliquez pour gérer les comptes utilisateurs</p>
      </div>
    </div>
  </Link>
);

// ---------- Slow-moving section ----------

const stockBadgeClass = (s) =>
  s === 0 ? 'text-red-500 bg-red-500/10'
    : s < 5 ? 'text-yellow-500 bg-yellow-500/10'
    : 'text-zinc-400 bg-white/5';

export const SlowMovingSection = ({ data, isAdmin }) => {
  if (!data?.products?.length) return null;
  const { products, suggestion } = data;
  return (
    <AdminCard className="p-5 mb-6 border-[color:rgb(var(--primary-rgb)/0.20)]" data-testid="slow-moving-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Lightbulb className="w-5 h-5 text-[color:var(--color-primary)]" />
          <div>
            <h3 className="text-white text-sm uppercase tracking-widest">Produits à faible rotation</h3>
            <p className="text-zinc-500 text-xs mt-0.5">{suggestion}</p>
          </div>
        </div>
        <span className="text-[color:var(--color-primary)] text-xs bg-[color:rgb(var(--primary-rgb)/0.10)] px-3 py-1">
          {products.length} produit{products.length > 1 ? 's' : ''}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-2 pr-4 text-zinc-500 text-xs uppercase tracking-wider">Produit</th>
              <th className="text-left py-2 px-4 text-zinc-500 text-xs uppercase tracking-wider">Marque</th>
              <th className="text-center py-2 px-4 text-zinc-500 text-xs uppercase tracking-wider">Stock</th>
              {isAdmin && <th className="text-right py-2 px-4 text-zinc-500 text-xs uppercase tracking-wider">Prix détail</th>}
              <th className="text-right py-2 pl-4 text-zinc-500 text-xs uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody data-testid="slow-moving-table">
            {products.map((p) => (
              <tr key={p.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[color:var(--color-bg)] overflow-hidden flex-shrink-0">
                      <img src={p.image_url} alt={p.name}
                        className="w-full h-full object-cover" onError={onImgErr} />
                    </div>
                    <span className="text-white text-sm truncate max-w-[180px]">{p.name}</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-zinc-400 text-sm">{p.brand}</td>
                <td className="py-3 px-4 text-center">
                  <span className={`text-xs px-2 py-0.5 ${stockBadgeClass(p.stock)}`}>{p.stock}</span>
                </td>
                {isAdmin && <td className="py-3 px-4 text-right text-[color:var(--color-primary)] text-sm">{p.price_detail}€</td>}
                <td className="py-3 pl-4 text-right">
                  <Link to={`/admin/produits/${p.id}`}
                    className="text-xs text-[color:var(--color-primary)] hover:underline"
                    data-testid={`slow-edit-${p.id}`}>
                    Modifier
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminCard>
  );
};

// ---------- Stats grid ----------

export const StatsGrid = ({ stats, isAdmin }) => (
  <div className={`grid gap-4 mb-8 ${isAdmin ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2 lg:grid-cols-3'}`}>
    {isAdmin && (
      <AdminKPI
        label="Revenus"
        value={`${(stats?.total_revenue || 0).toFixed(2)}€`}
        icon={DollarSign}
        rightSlot={<TrendingUp className="w-4 h-4 text-green-500" />}
        testid="stat-revenue"
        to="/admin/statistiques"
      />
    )}
    <AdminKPI
      label="Commandes"
      value={stats?.total_orders || 0}
      icon={ShoppingCart}
      rightSlot={stats?.pending_orders > 0 && (
        <span className="text-[10px] text-yellow-500 bg-yellow-500/10 px-2 py-0.5">
          {stats.pending_orders} en attente
        </span>
      )}
      testid="stat-orders"
      to="/admin/commandes"
    />
    <AdminKPI
      label="Produits"
      value={stats?.total_products || 0}
      icon={Package}
      rightSlot={(stats?.low_stock > 0 || stats?.out_of_stock > 0) && (
        <AlertTriangle className="w-4 h-4 text-red-500" />
      )}
      testid="stat-products"
      to={stats?.low_stock > 0 ? '/admin/produits?filter=low_stock' : '/admin/produits'}
    />
    {isAdmin && (
      <AdminKPI
        label="Clients"
        value={stats?.total_customers || 0}
        icon={Users}
        testid="stat-customers"
        to="/admin/utilisateurs"
      />
    )}
  </div>
);

// ---------- Recent orders panel ----------

const STATUS_COLORS = {
  pending: 'text-yellow-500',
  processing: 'text-purple-500',
  ready: 'text-blue-500',
  shipped: 'text-cyan-500',
  delivered: 'text-green-500',
  cancelled: 'text-red-500',
  // Legacy
  confirmed: 'text-blue-500',
};
const STATUS_LABELS = {
  pending: 'En attente',
  processing: 'En préparation',
  ready: 'Terminée',
  shipped: 'Envoyée',
  delivered: 'Livrée',
  cancelled: 'Annulée',
  // Legacy
  confirmed: 'Terminée',
};

export const RecentOrdersPanel = ({ orders = [] }) => (
  <AdminCard className="p-5">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-white text-sm uppercase tracking-widest">Commandes Récentes</h3>
      <Link to="/admin/commandes" className="text-[color:var(--color-primary)] text-xs hover:underline">Voir tout</Link>
    </div>
    {orders.length > 0 ? (
      <div className="space-y-3">
        {orders.map((order) => (
          <div key={order.id}
            className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
            <div>
              <p className="text-white text-sm">{order.customer_name}</p>
              <p className="text-zinc-600 text-xs flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {new Date(order.created_at).toLocaleDateString('fr-FR')}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[color:var(--color-primary)] text-sm">{order.total.toFixed(2)}€</p>
              <p className={`text-xs ${STATUS_COLORS[order.status] || 'text-zinc-500'}`}>
                {STATUS_LABELS[order.status] || order.status}
              </p>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <AdminEmpty>Aucune commande récente</AdminEmpty>
    )}
  </AdminCard>
);

// ---------- Low-stock panel ----------

export const LowStockPanel = ({ products = [], wide = false }) => (
  <AdminCard className={`p-5 ${wide ? 'lg:col-span-2' : ''}`}>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-white text-sm uppercase tracking-widest">Alertes Stock</h3>
      <Link to="/admin/produits" className="text-[color:var(--color-primary)] text-xs hover:underline">Gérer</Link>
    </div>
    {products.length > 0 ? (
      <div className="space-y-3">
        {products.slice(0, 5).map((p) => (
          <Link key={p.id} to={`/admin/produits/${p.id}`}
            className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 hover:bg-white/5 -mx-2 px-2 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[color:var(--color-bg)] overflow-hidden flex-shrink-0">
                <img src={p.image_url} alt={p.name}
                  className="w-full h-full object-cover" onError={onImgErr} />
              </div>
              <p className="text-white text-sm truncate">{p.name}</p>
            </div>
            <span className={`text-xs px-2 py-0.5 flex-shrink-0 ${
              p.stock === 0 ? 'text-red-500 bg-red-500/10'
                : 'text-yellow-500 bg-yellow-500/10'
            }`}>
              {p.stock === 0 ? 'Rupture' : `${p.stock}`}
            </span>
          </Link>
        ))}
      </div>
    ) : (
      <div className="text-center py-6">
        <p className="text-green-500 text-sm">Tous les stocks sont OK</p>
      </div>
    )}
  </AdminCard>
);
